import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { articlesAPI, categoriesAPI } from '../utils/api';
import { ArticleCard } from '../components/ArticleCard';
import { Sidebar } from '../components/Sidebar';
import { MagazineHero } from '../components/MagazineHero';
import { SEO } from '../components/SEO';
import { TrendingUp, ArrowRight, Layers, ChevronRight } from 'lucide-react';
import { useRegion } from '../context/RegionContext';
import { getArticleImage, handleImageError } from '../utils/imageUtils';

const HOME_CACHE_TTL_MS = 5 * 60 * 1000;

export const HomePage = () => {
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [trendingArticles, setTrendingArticles] = useState([]);
  const [latestArticles, setLatestArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentRegion } = useRegion();

  useEffect(() => {
    let isMounted = true;
    const cacheKey = `homepage:${currentRegion}`;

    try {
      const cachedData = sessionStorage.getItem(cacheKey);
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        const isFresh = Date.now() - parsed.timestamp < HOME_CACHE_TTL_MS;
        if (isFresh) {
          setFeaturedArticles(parsed.featuredArticles || []);
          setTrendingArticles(parsed.trendingArticles || []);
          setLatestArticles(parsed.latestArticles || []);
          setCategories(parsed.categories || []);
          setLoading(false);
        }
      }
    } catch (error) {
      console.warn('Failed to read homepage cache:', error);
    }

    // Prevent infinite spinner on slow backend cold starts.
    const loadingSafetyTimeout = setTimeout(() => {
      if (isMounted) {
        setLoading(false);
      }
    }, 4000);

    const fetchData = async () => {
      try {
        const region = currentRegion !== 'global' ? currentRegion : undefined;
        const [featuredRes, articlesRes, categoriesRes] = await Promise.allSettled([
          articlesAPI.getAll({ is_featured: true, is_published: true, region: currentRegion !== 'global' ? currentRegion : undefined, limit: 5 }),
          articlesAPI.getAll({ is_published: true, region: currentRegion !== 'global' ? currentRegion : undefined, limit: 20 }),
          categoriesAPI.getAll()
        ]);

        if (!isMounted) return;

        const nextFeatured = featuredRes.status === 'fulfilled' ? featuredRes.value.data : [];
        const nextArticles = articlesRes.status === 'fulfilled' ? articlesRes.value.data : [];
        const nextCategories = categoriesRes.status === 'fulfilled' ? categoriesRes.value.data : [];

        setFeaturedArticles(nextFeatured);
        setTrendingArticles(nextArticles.slice().sort((a, b) => b.views - a.views).slice(0, 5));
        setLatestArticles(nextArticles.slice(0, 12));
        setCategories(nextCategories);

        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({
            timestamp: Date.now(),
            region,
            featuredArticles: nextFeatured,
            trendingArticles: nextArticles.slice().sort((a, b) => b.views - a.views).slice(0, 5),
            latestArticles: nextArticles.slice(0, 12),
            categories: nextCategories,
          }));
        } catch (error) {
          console.warn('Failed to write homepage cache:', error);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      clearTimeout(loadingSafetyTimeout);
    };
  }, [currentRegion]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="loading-spinner">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-brand-red mx-auto mb-4"></div>
          <p className="text-gray-600 font-inter">Loading amazing deals...</p>
        </div>
      </div>
    );
  }

  // Sample featured deal for hero
  const featuredDeal = {
    title: "Extra 20% Off Electronics",
    description: "Use code SAVE20 for additional savings on all tech products this week.",
    discount: "20% OFF",
    link: "/category/deals-offers"
  };

  return (
    <>
      <SEO 
        title="Best Deals, Coupons & Shopping Guides"
        description="Discover the best deals, exclusive coupons, and expert shopping guides. Save money on electronics, fashion, travel, and more with iShopGenie."
        type="website"
      />

      <div className="min-h-screen bg-gray-50" data-testid="homepage">
        {/* Magazine Hero Section */}
        {featuredArticles.length > 0 && (
          <MagazineHero 
            featuredArticles={featuredArticles} 
            featuredDeal={featuredDeal}
          />
        )}

        {/* Main Content Area with Sidebar */}
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content - 70% */}
            <main className="flex-1 lg:max-w-[70%]">
              {/* Latest Deals & Guides Section */}
              <section className="mb-12" data-testid="latest-section">
                {/* Section Header */}
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-5 h-5 text-brand-red" />
                      <span className="text-brand-red font-semibold uppercase tracking-wide text-sm">Fresh Content</span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-playfair font-bold text-gray-900">
                      Latest Deals & Guides
                    </h2>
                  </div>
                  <Link 
                    to="/search" 
                    className="hidden sm:flex items-center gap-2 text-brand-red hover:text-red-700 font-semibold smooth-transition"
                  >
                    View All
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* ROW 1: Featured Article (60%) + 2 Stacked Articles (40%) */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
                  {/* Large Featured Article - 60% width (7 cols) */}
                  {latestArticles[0] && (
                    <div className="lg:col-span-7">
                      <Link 
                        to={`/article/${latestArticles[0].slug}`} 
                        className="group block h-full"
                        data-testid={`featured-article-${latestArticles[0].slug}`}
                      >
                        <div className="bg-white rounded-xl overflow-hidden h-full flex flex-col border border-gray-100 hover:shadow-xl smooth-transition">
                          <div className="relative aspect-[16/10] overflow-hidden">
                            <img
                              src={getArticleImage(latestArticles[0])}
                              alt={latestArticles[0].title}
                              className="w-full h-full object-cover group-hover:scale-105 smooth-transition duration-500"
                              loading="eager"
                              onError={handleImageError}
                            />
                            <div className="absolute top-4 left-4">
                              <span className="px-3 py-1.5 bg-brand-red text-white text-xs font-bold uppercase tracking-wider rounded-lg shadow-lg">
                                {latestArticles[0].category_name}
                              </span>
                            </div>
                          </div>
                          <div className="p-6 flex-1 flex flex-col">
                            <h3 className="text-2xl font-playfair font-bold text-gray-900 mb-3 group-hover:text-brand-red smooth-transition leading-tight line-clamp-2">
                              {latestArticles[0].title}
                            </h3>
                            <p className="text-gray-600 leading-relaxed mb-4 line-clamp-3 flex-1">
                              {latestArticles[0].excerpt}
                            </p>
                            <div className="flex items-center justify-between text-sm text-gray-500">
                              <span>{latestArticles[0].reading_time} min read</span>
                              <span className="text-brand-red font-semibold flex items-center gap-1 group-hover:gap-2 smooth-transition">
                                Read More <ArrowRight className="w-4 h-4" />
                              </span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  )}

                  {/* Two Stacked Articles - 40% width (5 cols) */}
                  <div className="lg:col-span-5 flex flex-col gap-6">
                    {latestArticles.slice(1, 3).map((article) => (
                      <Link 
                        key={article.id}
                        to={`/article/${article.slug}`} 
                        className="group flex-1"
                        data-testid={`stacked-article-${article.slug}`}
                      >
                        <div className="bg-white rounded-xl overflow-hidden h-full flex border border-gray-100 hover:shadow-lg smooth-transition">
                          <div className="w-2/5 relative overflow-hidden">
                            <img
                              src={getArticleImage(article)}
                              alt={article.title}
                              className="w-full h-full object-cover group-hover:scale-105 smooth-transition duration-500"
                              loading="lazy"
                              onError={handleImageError}
                            />
                          </div>
                          <div className="w-3/5 p-4 flex flex-col justify-center">
                            <span className="text-xs text-brand-red font-semibold uppercase tracking-wide mb-2">
                              {article.category_name}
                            </span>
                            <h3 className="font-playfair font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-brand-red smooth-transition leading-snug">
                              {article.title}
                            </h3>
                            <p className="text-sm text-gray-500 line-clamp-2 mb-2">
                              {article.excerpt}
                            </p>
                            <span className="text-xs text-gray-400">{article.reading_time} min read</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* ROW 2: Three Equal Article Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {latestArticles.slice(3, 6).map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              </section>

              {/* More Articles Section */}
              {latestArticles.length > 6 && (
                <section className="mb-12" data-testid="more-articles-section">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-playfair font-bold text-gray-900">More Deals & Guides</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {latestArticles.slice(6, 10).map((article) => (
                      <ArticleCard key={article.id} article={article} variant="compact" />
                    ))}
                  </div>
                </section>
              )}

              {/* Categories Section */}
              {categories.length > 0 && (
                <section data-testid="categories-section">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Layers className="w-5 h-5 text-brand-red" />
                        <span className="text-brand-red font-semibold uppercase tracking-wide text-sm">Explore</span>
                      </div>
                      <h2 className="text-2xl font-playfair font-bold text-gray-900">
                        Browse by Category
                      </h2>
                    </div>
                    <Link 
                      to="/search" 
                      className="text-brand-red hover:text-red-700 font-semibold text-sm flex items-center gap-1"
                    >
                      All Categories <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {categories.slice(0, 6).map((category) => (
                      <Link
                        key={category.id}
                        to={`/category/${category.slug}`}
                        className="group bg-white rounded-xl p-5 hover:shadow-lg smooth-transition border border-gray-100 hover:border-brand-red"
                        data-testid={`category-card-${category.slug}`}
                      >
                        <h3 className="font-playfair font-bold text-lg text-gray-900 group-hover:text-brand-red smooth-transition mb-1">
                          {category.name}
                        </h3>
                        {category.description && (
                          <p className="text-sm text-gray-500 line-clamp-2">
                            {category.description}
                          </p>
                        )}
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </main>

            {/* Sidebar - 30% */}
            <aside className="lg:w-[30%] lg:min-w-[320px]">
              <Sidebar trendingArticles={trendingArticles} />
            </aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default HomePage;
